export const PRODUCT_TYPES = [
    { key: 'PCProduct', translationKey: 'pc_product', fallback: 'Work' },
    { key: 'MobileProduct', translationKey: 'mobile_product', fallback: 'Mobile' },
    { key: 'StorageProduct', translationKey: 'storage_product', fallback: 'Single disk-drives' },
    { key: 'WatchProduct', translationKey: 'watch', fallback: 'Watch' },
    { key: 'HeadphoneProduct', translationKey: 'headphone', fallback: 'Headphone' }
];

export const TRIGGER_DEFINITIONS = [
    { name: 'TriggerOnSessionStart', translationKey: 'on_start_session', fallback: 'On start session', default: true },
    { name: 'TriggerOnModifiedWeb', translationKey: 'external_trigger_on_modified_web', fallback: 'On WebUI modifications', default: true },
    { name: 'TriggerOnModifiedProcessing', translationKey: 'external_trigger_on_modified_processing', fallback: 'On processing modifications', default: true },
    { name: 'TriggerOnErasureProgress', translationKey: 'external_trigger_on_erasure_progress', fallback: 'On erasure progress', default: false },
    { name: 'TriggerOnSessionEnd', translationKey: 'on_end_session', fallback: 'On end session', default: true },
    { name: 'TriggerOnErasureFinished', translationKey: 'on_erasure_finished', fallback: 'On erasure finished', default: true },
    { name: 'TriggerOnDiagnoseFinished', translationKey: 'on_diagnose_finished', fallback: 'On diagnose finished', default: true },
    { name: 'TriggerOnAnalyzeFinished', translationKey: 'on_analyze_finished', fallback: 'On analyze finished', default: false },
    { name: 'TriggerOnCompleteDriveErasureFinished', translationKey: 'on_complete_drive_erasure_finished', fallback: 'On complete drive erasure finished', default: false }
];

const ALL_TRIGGER_NAMES = TRIGGER_DEFINITIONS.map(trigger => trigger.name);
const withoutTriggers = excluded => ALL_TRIGGER_NAMES.filter(name => !excluded.includes(name));

// On analyze finished and On complete drive erasure finished are Work-only.
export const PRODUCT_TYPE_TRIGGERS = {
    PCProduct: ALL_TRIGGER_NAMES,
    MobileProduct: withoutTriggers(['TriggerOnAnalyzeFinished', 'TriggerOnCompleteDriveErasureFinished']),
    StorageProduct: withoutTriggers(['TriggerOnSessionStart', 'TriggerOnSessionEnd', 'TriggerOnAnalyzeFinished', 'TriggerOnDiagnoseFinished', 'TriggerOnCompleteDriveErasureFinished']),
    WatchProduct: withoutTriggers(['TriggerOnAnalyzeFinished', 'TriggerOnCompleteDriveErasureFinished']),
    HeadphoneProduct: withoutTriggers(['TriggerOnErasureProgress', 'TriggerOnErasureFinished', 'TriggerOnAnalyzeFinished', 'TriggerOnCompleteDriveErasureFinished'])
};

// Confirm data export is Work-only: the other product types have no data-export step
// to confirm.
export const PRODUCT_TYPE_BEHAVIOURS = {
    PCProduct: ['SendPerformedDiagnoseCasesOnly', 'ConfirmDataExport'],
    MobileProduct: ['SendPerformedDiagnoseCasesOnly'],
    StorageProduct: [],
    WatchProduct: ['SendPerformedDiagnoseCasesOnly'],
    HeadphoneProduct: ['SendPerformedDiagnoseCasesOnly']
};

// What each external system type can configure per product type. Mirrors what the
// backend persists for that type, so a form never offers a setting its own
// create*Setting builder would drop.
//   triggers          - the per-product-type trigger checkboxes
//   report            - 'full' (trigger, all three conditions, method and endpoint),
//                       'basic' (trigger and the three conditions, no method/endpoint) or false
//   diagnoseCases     - "Send performed diagnose cases only"
//   provideExternalID - Provide External ID on device check and its fields ('in' direction)
//   confirmDataExport - Confirm data export and its message ('out' direction)
export const EXTERNAL_TYPE_CAPABILITIES = {
    json: { triggers: true, report: 'full', diagnoseCases: true, provideExternalID: true, confirmDataExport: true },
    database: { triggers: true, report: false, diagnoseCases: false, provideExternalID: false, confirmDataExport: false },
    servicenow: { triggers: true, report: 'basic', diagnoseCases: false, provideExternalID: false, confirmDataExport: false },
    kloudless: { triggers: true, report: false, diagnoseCases: false, provideExternalID: false, confirmDataExport: false },
    makor: { triggers: false, report: false, diagnoseCases: false, provideExternalID: true, confirmDataExport: true },
    store_external_data: { triggers: false, report: false, diagnoseCases: false, provideExternalID: true, confirmDataExport: false }
};

const NO_CAPABILITIES = { triggers: false, report: false, diagnoseCases: false, provideExternalID: false, confirmDataExport: false };

export const capabilitiesForType = type => EXTERNAL_TYPE_CAPABILITIES[type] ?? NO_CAPABILITIES;

// Builds the ProductTypeSettings initial values for an external system setting.
// Pass the parsed Settings of an existing setting (Edit) to seed each product type from
// the stored per-type values, falling back to the legacy flat keys, then to the defaults.
// Only the keys the external type actually supports are built. Keys that are not
// applicable for a product type are always forced to false so the backend can tell
// "deliberately off" apart from "not configured".
export const buildProductTypeSettings = (external = null, externalType = 'json') => {
    const capabilities = capabilitiesForType(externalType);
    const provideExternalID = external?.ExternalSystemBehaviour?.ProvideExternalID;
    const confirmDataExport = external?.ExternalSystemBehaviour?.ConfirmDataExport;
    const settings = {};

    for (const { key: type } of PRODUCT_TYPES) {
        const perType = external?.ProductTypeSettings?.[type] ?? {};
        const typeSettings = {};

        if (capabilities.triggers) {
            for (const trigger of TRIGGER_DEFINITIONS) {
                typeSettings[trigger.name] = PRODUCT_TYPE_TRIGGERS[type].includes(trigger.name)
                    ? perType[trigger.name] ?? external?.[trigger.name] ?? trigger.default
                    : false;
            }
        }

        if (capabilities.report) {
            typeSettings.TriggerOnReportGenerated = perType.TriggerOnReportGenerated ?? external?.TriggerOnReportGenerated ?? false;
            typeSettings.SendReportOnSuccessfulErasure = perType.SendReportOnSuccessfulErasure ?? external?.SendReportOnSuccessfulErasure ?? false;
            typeSettings.SendReportOnFailedErasure = perType.SendReportOnFailedErasure ?? external?.SendReportOnFailedErasure ?? false;
            typeSettings.SendReportOnNoErasure = perType.SendReportOnNoErasure ?? external?.SendReportOnNoErasure ?? false;
        }

        if (capabilities.report === 'full') {
            typeSettings.SendReportMethod = perType.SendReportMethod ?? external?.SendReportMethod ?? 'base64pdf';
            typeSettings.SendErasureReportEndpoint = perType.SendErasureReportEndpoint ?? external?.SendErasureReportEndpoint ?? '';
        }

        if (capabilities.diagnoseCases) {
            typeSettings.SendPerformedDiagnoseCasesOnly = PRODUCT_TYPE_BEHAVIOURS[type].includes('SendPerformedDiagnoseCasesOnly')
                ? perType.SendPerformedDiagnoseCasesOnly ?? external?.SendPerformedDiagnoseCasesOnly ?? true
                : false;
        }

        if (capabilities.provideExternalID) {
            typeSettings.ProvideExternalIDEnabled = perType.ProvideExternalIDEnabled ?? provideExternalID?.[type]?.Enabled ?? false;
            typeSettings.ProvideExternalIDOptional = perType.ProvideExternalIDOptional ?? provideExternalID?.[type]?.Optional ?? false;
            typeSettings.ProvideExternalIDAllowDuplicate = perType.ProvideExternalIDAllowDuplicate ?? provideExternalID?.[type]?.AllowDuplicate ?? false;
            typeSettings.ProvideExternalIDRegexp = perType.ProvideExternalIDRegexp ?? provideExternalID?.[type]?.Regexp ?? provideExternalID?.Regexp ?? '';
            typeSettings.ProvideExternalIDDescription = perType.ProvideExternalIDDescription ?? provideExternalID?.[type]?.Description ?? provideExternalID?.Description ?? '';
            typeSettings.ProvideExternalIDName = perType.ProvideExternalIDName ?? provideExternalID?.[type]?.Name ?? provideExternalID?.Name ?? '';
        }

        if (capabilities.confirmDataExport) {
            typeSettings.ConfirmDataExportEnabled = PRODUCT_TYPE_BEHAVIOURS[type].includes('ConfirmDataExport')
                ? perType.ConfirmDataExportEnabled ?? confirmDataExport?.[type]?.Enabled ?? confirmDataExport?.Enabled ?? false
                : false;
            typeSettings.ConfirmDataExportMessage = perType.ConfirmDataExportMessage ?? confirmDataExport?.[type]?.Message ?? confirmDataExport?.Message ?? '';
        }

        settings[type] = typeSettings;
    }

    return settings;
};

export const defaultJSONMapping = `
{
    "PCProduct": {
        "ComingFrom": "Securaze",
        "Type": "PC",
        "Name": "{Model}",
        "Vendor": "{Vendor}",
        "AssetID": "{ExternalID}",
        "SerialNumber": "{SerialNumber}",
        "Chassis": "{Chassis}",
        "OperatingSystem": "{OS}",
        "DVD": "{DVD}",
        "RAM": "{RAM}",
        "DisplaySize": "{DisplaySize}",
        "CreatedAt": "{CreatedAt}",
        "InternationalGrading": "{Grade}",
        "TypicalProblems": "{TypicalProblems}",
        "DiskDrives": "{Disks}",
        "Processors": "{CPUComponents}",
        "Batteries": "{BatteryComponents}",
        "Motherboards": "{MotherboardComponents}",
        "GPUs": "{GPUComponents}",
        "NetworkInterfaces":
        "{NetworkComponents}",
        "Memory": "{MemoryComponents}",
        "LabelComment1": "{LabelComment1}",
        "LabelComment2": "{LabelComment2}",
        "LabelComment3": "{LabelComment3}",
        "LabelComment4": "{LabelComment4}",
        "CommaSeparatedWipeMethods": "{Disks[,].WipeMethod}",
        "TypicalProblemCodes": "{TypicalProblemCodes}",
        "SecurazeID": "{SecurazeID}",
        "HardwareID": "{HardwareID}",
        "DiagnoseResults": "{DiagnoseResults}",
        "Container": "{Colli}"
    },
    "MobileProduct": {
        "ComingFrom": "Securaze",
        "Type": "Mobile",
        "Name": "{Model}",
        "Vendor": "{Vendor}",
        "AssetID": "{ExternalID}",
        "SerialNumber": "{SerialNumber}",
        "Chassis": "{Chassis}",
        "OperatingSystem": "{OS}",
        "IMEI": "{IMEI}",
        "IMEI2": "{IMEI2}",
        "InternationalGrading": "{Grade}",
        "TypicalProblems": "{TypicalProblems}",
        "DiskDrives": "{Disks}",
        "Processors": "{CPUComponents}",
        "Batteries": "{BatteryComponents}",
        "Motherboards": "{MotherboardComponents}",
        "GPUs": "{GPUComponents}",
        "NetworkInterfaces": "{NetworkComponents}",
        "Memory": "{MemoryComponents}",
        "LabelComment1": "{LabelComment1}",
        "LabelComment2": "{LabelComment2}",
        "LabelComment3": "{LabelComment3}",
        "LabelComment4": "{LabelComment4}",
        "CommaSeparatedWipeMethods": "{Disks[,].WipeMethod}",
        "TypicalProblemCodes": "{TypicalProblemCodes}",
        "SecurazeID": "{SecurazeID}",
        "HardwareID": "{HardwareID}",
        "DiagnoseResults": "{DiagnoseResults}",
        "Container": "{Colli}"
    },
    "StorageProduct": {},
    "DisplayProduct": {},
    "WatchProduct": {
        "ComingFrom": "Securaze",
        "Type": "Watch",
        "Name": "{Model}",
        "Vendor": "{Vendor}",
        "AssetID": "{ExternalID}",
        "SerialNumber": "{SerialNumber}",
        "Chassis": "{Chassis}",
        "OperatingSystem": "{OS}",
        "IMEI": "{IMEI}",
        "InternationalGrading": "{Grade}",
        "TypicalProblems": "{TypicalProblems}",
        "DiskDrives": "{Disks}",
        "Batteries": "{BatteryComponents}",
        "LabelComment1": "{LabelComment1}",
        "LabelComment2": "{LabelComment2}",
        "LabelComment3": "{LabelComment3}",
        "LabelComment4": "{LabelComment4}",
        "CommaSeparatedWipeMethods": "{Disks[,].WipeMethod}",
        "TypicalProblemCodes": "{TypicalProblemCodes}",
        "SecurazeID": "{SecurazeID}",
        "HardwareID": "{HardwareID}",
        "DiagnoseResults": "{DiagnoseResults}",
        "Container": "{Colli}"
    },
    "HeadphoneProduct": {
        "ComingFrom": "Securaze",
        "Type": "Headphone",
        "Name": "{Model}",
        "Vendor": "{Vendor}",
        "AssetID": "{ExternalID}",
        "SerialNumber": "{SerialNumber}",
        "Chassis": "{Chassis}",
        "InternationalGrading": "{Grade}",
        "TypicalProblems": "{TypicalProblems}",
        "Batteries": "{BatteryComponents}",
        "LabelComment1": "{LabelComment1}",
        "LabelComment2": "{LabelComment2}",
        "LabelComment3": "{LabelComment3}",
        "LabelComment4": "{LabelComment4}",
        "TypicalProblemCodes": "{TypicalProblemCodes}",
        "SecurazeID": "{SecurazeID}",
        "HardwareID": "{HardwareID}",
        "DiagnoseResults": "{DiagnoseResults}",
        "Container": "{Colli}"
    }
}
`;
